import { useGameStore } from '../store';
import { getCurrentTier, getNextTier } from '../data/housing';

/**
 * Housing visualization component with tier-appropriate styling
 * Displays current housing tier with pixel-art style visuals
 */
export function HousingDisplay() {
  const housingLevel = useGameStore((state) => state.housingLevel);
  const currentTier = getCurrentTier(housingLevel);
  const nextTier = getNextTier(housingLevel);

  if (!currentTier) return null;

  // Determine visual style based on tier
  const getTierStyles = () => {
    if (housingLevel >= 6) {
      // Tier 6: Beachfront Estate - Epic purple with gold accents
      return {
        container: 'border-purple-500 bg-purple-900/30',
        accent: 'text-purple-400',
        nameColor: 'text-purple-300',
        building: 'border-purple-400 bg-purple-800/50'
      };
    } else if (housingLevel >= 5) {
      // Tier 5: Penthouse - Gold luxury
      return {
        container: 'border-gold bg-yellow-900/20',
        accent: 'text-gold',
        nameColor: 'text-gold',
        building: 'border-gold bg-yellow-800/30'
      };
    } else if (housingLevel >= 3) {
      // Tier 3-4: Houses - Teal with warmth
      return {
        container: 'border-teal bg-teal/10',
        accent: 'text-teal',
        nameColor: 'text-teal',
        building: 'border-teal bg-teal/20'
      };
    } else {
      // Tier 0-2: Apartments - Basic teal
      return {
        container: 'border-gray-500 bg-gray-800/30',
        accent: 'text-gray-400',
        nameColor: 'text-gray-300',
        building: 'border-gray-500 bg-gray-700/30'
      };
    }
  };

  const styles = getTierStyles();

  // Render tier-appropriate building visual
  const renderBuilding = () => {
    if (housingLevel >= 5) {
      // Luxury: Large multi-section building
      return (
        <div className="flex flex-col items-center">
          {/* Penthouse/Estate - Large with multiple sections */}
          <div className={`w-20 h-4 border-2 ${styles.building} mb-0.5`}>
            <div className="flex justify-around items-center h-full">
              <div className="w-2 h-2 bg-yellow-400/60"></div>
              <div className="w-2 h-2 bg-yellow-400/60"></div>
              <div className="w-2 h-2 bg-yellow-400/60"></div>
            </div>
          </div>
          <div className={`w-24 h-12 border-2 ${styles.building}`}>
            <div className="grid grid-cols-4 gap-1 p-1 h-full">
              <div className="bg-yellow-400/40"></div>
              <div className="bg-yellow-400/60"></div>
              <div className="bg-yellow-400/40"></div>
              <div className="bg-yellow-400/60"></div>
              <div className="bg-yellow-400/60"></div>
              <div className="bg-yellow-400/40"></div>
              <div className="bg-yellow-400/60"></div>
              <div className="bg-yellow-400/40"></div>
            </div>
          </div>
          <div className={`w-28 h-4 border-2 border-t-0 ${styles.building}`}></div>
        </div>
      );
    } else if (housingLevel >= 3) {
      // House: Medium box with roof triangle
      return (
        <div className="flex flex-col items-center">
          {/* Roof */}
          <div className="w-0 h-0 border-l-[24px] border-r-[24px] border-b-[16px] border-l-transparent border-r-transparent border-b-teal"></div>
          {/* House body */}
          <div className={`w-12 h-10 border-2 ${styles.building}`}>
            <div className="flex justify-around items-start pt-1">
              <div className="w-2 h-3 bg-teal/50"></div>
              <div className="w-3 h-5 bg-teal/70 mt-2"></div>
              <div className="w-2 h-3 bg-teal/50"></div>
            </div>
          </div>
        </div>
      );
    } else {
      // Apartment: Small box
      return (
        <div className="flex flex-col items-center">
          {/* Apartment building */}
          <div className={`w-10 h-12 border-2 ${styles.building}`}>
            <div className="grid grid-cols-2 gap-0.5 p-1 h-full">
              <div className="bg-gray-400/40"></div>
              <div className="bg-gray-400/60"></div>
              <div className="bg-gray-400/60"></div>
              <div className="bg-gray-400/40"></div>
              <div className="bg-gray-400/40"></div>
              <div className="bg-gray-400/60"></div>
            </div>
          </div>
        </div>
      );
    }
  };

  return (
    <div className={`p-4 border-2 rounded-lg ${styles.container} mt-4 min-w-[200px]`}>
      {/* Building Visual */}
      <div className="flex justify-center mb-3">
        {renderBuilding()}
      </div>

      {/* Tier Name */}
      <h3 className={`font-pixel text-lg text-center ${styles.nameColor}`}>
        {currentTier.name}
      </h3>

      {/* Description */}
      <p className={`text-xs text-center mt-1 ${styles.accent}`}>
        {currentTier.description}
      </p>

      {/* Next Tier Preview */}
      {nextTier && (
        <div className="mt-3 pt-2 border-t border-gray-600">
          <p className="text-xs text-gray-500 text-center">
            Next: <span className="text-gray-400">{nextTier.name}</span>
          </p>
          <p className="text-xs text-gray-500 text-center">
            Cost: <span className="text-gold">{nextTier.cost.toLocaleString()} M</span>
          </p>
        </div>
      )}

      {/* Max Tier Indicator */}
      {!nextTier && (
        <div className="mt-3 pt-2 border-t border-purple-600">
          <p className="text-xs text-purple-400 text-center font-pixel">
            Maximum Tier Achieved!
          </p>
        </div>
      )}
    </div>
  );
}
